import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, AlertCircle, CheckCircle2, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import GlassCard from "@/components/GlassCard";

export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState("");

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (!email.includes("@") || !email.includes(".")) {
            setError("Please enter a valid email address.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/api/auth/forgot-password", { email: email.trim().toLowerCase() });
            setSuccess(true);
        } catch (err) {
            const detail = err.response?.data?.detail;
            setError(detail || "Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-dvh flex items-center justify-center px-4 py-24">
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="w-full max-w-md"
            >
                <Link to="/login" className="inline-flex items-center gap-1.5 text-text-secondary hover:text-text-primary text-sm mb-6 transition-colors">
                    <ArrowLeft size={14} /> Back to sign in
                </Link>

                <GlassCard className="p-8" hover={false}>
                    <div className="mb-6">
                        <h1 className="text-2xl font-bold text-text-primary mb-2">Forgot your password?</h1>
                        <p className="text-text-secondary text-sm">
                            Enter the email you registered with. We'll send you a link to reset your password.
                        </p>
                    </div>

                    {success ? (
                        <div className="text-center py-4">
                            <CheckCircle2 size={40} className="text-green-pioneer mx-auto mb-4" />
                            <p className="text-text-primary font-semibold mb-2">Check your inbox</p>
                            <p className="text-text-secondary text-sm">
                                If <span className="font-mono text-blue-primary">{email}</span> is registered,
                                you'll receive a reset link within a few minutes.
                            </p>
                            <p className="text-text-secondary text-xs mt-4">
                                Didn't receive it? Check your spam folder, or{" "}
                                <button onClick={() => { setSuccess(false); setEmail(""); }}
                                    className="text-blue-primary hover:underline">
                                    try again
                                </button>.
                            </p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {error && (
                                <div className="flex items-center gap-2 bg-red-starter/10 border border-red-starter/20 rounded-xl px-4 py-2.5">
                                    <AlertCircle size={14} className="text-red-starter shrink-0" />
                                    <p className="text-xs text-red-starter">{error}</p>
                                </div>
                            )}
                            <div className="glass-input-wrap">
                                <Mail size={16} className="field-icon" />
                                <input
                                    type="email"
                                    placeholder="Email address"
                                    value={email}
                                    onChange={e => { setEmail(e.target.value); if (error) setError(""); }}
                                    className="glass-input text-sm"
                                    required
                                    autoFocus
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={submitting || !email.trim()}
                                className="w-full bg-blue-primary text-white py-2.5 rounded-xl font-medium text-sm hover:bg-blue-primary/90 transition-all disabled:opacity-50"
                            >
                                {submitting ? "Sending reset link…" : "Send reset link"}
                            </button>
                        </form>
                    )}
                </GlassCard>
            </motion.div>
        </div>
    );
}
